import { ReaderCatalogTagMetricPill } from '@/components/ReadersTable/ReaderCatalogTagPill';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import { DeleteIcon } from '@/components/ui/Icons';

export type AuthorSheetsExtractorResultRow = {
  id: string;
  fileName: string;
  authorSheetsDisplay: string;
  charactersDisplay: string;
  pagesDisplay: string;
  errorMessage?: string;
};

type AuthorSheetsExtractorResultCardProps = {
  row: AuthorSheetsExtractorResultRow;
  busy: boolean;
  onRemove: (id: string) => void;
};

/** Чуть темнее прежнего светло-серого — лучше читается белый текст на градиенте */
const METRIC_LIGHT_SILVER_HEX = '#97a1b2';
/** При ошибке строки — нейтральный серый для всех трёх бейджей */
const METRIC_ROW_ERROR_HEX = '#7d8694';

function parseAuthorSheetsNumber(display: string): number | null {
  const t = display.trim().replace(/\s/g, '').replace(',', '.');
  if (!t || t.toUpperCase() === 'N/A') return null;
  const n = Number.parseFloat(t);
  return Number.isFinite(n) ? n : null;
}

/**
 * Градация АЛ (белый текст на градиенте): 0–3 светло-зелёный, 3–5 зелёный, 5–10 жёлтый,
 * 10–15 оранжевый, 15–20 красный, 20–30 тёмно-серый, >30 чёрный.
 */
function authorSheetsTierAccentHex(al: number): string {
  if (al < 0) return METRIC_ROW_ERROR_HEX;
  if (al < 3) return '#9ccc65';
  if (al < 5) return '#388e3c';
  if (al < 10) return '#fbc02d';
  if (al < 15) return '#fb8c00';
  if (al < 20) return '#f00';
  if (al < 30) return '#455a64';
  return '#000';
}

export function AuthorSheetsExtractorResultCard({ row, busy, onRemove }: AuthorSheetsExtractorResultCardProps) {
  const valueMuted = Boolean(row.errorMessage);
  const charColor = valueMuted ? METRIC_ROW_ERROR_HEX : METRIC_LIGHT_SILVER_HEX;
  const pagesColor = valueMuted ? METRIC_ROW_ERROR_HEX : METRIC_LIGHT_SILVER_HEX;
  const alParsed = parseAuthorSheetsNumber(row.authorSheetsDisplay);
  const alColor = valueMuted
    ? METRIC_ROW_ERROR_HEX
    : alParsed != null
      ? authorSheetsTierAccentHex(alParsed)
      : METRIC_ROW_ERROR_HEX;

  return (
    <li className="author-sheets-mini-app__item">
      <div className="author-sheets-mini-app__item-main">
        <span className="author-sheets-mini-app__item-name" title={row.fileName}>
          {row.fileName}
        </span>
        <div className="author-sheets-mini-app__metrics" role="group" aria-label="Метрики файла">
          <ReaderCatalogTagMetricPill value={row.charactersDisplay} suffix="СИМВОЛОВ" color={charColor} />
          <ReaderCatalogTagMetricPill value={row.pagesDisplay} suffix="СТР" color={pagesColor} />
          <ReaderCatalogTagMetricPill value={row.authorSheetsDisplay} suffix="АЛ" color={alColor} />
        </div>
        {row.errorMessage ? (
          <span className="author-sheets-mini-app__item-error" title={row.errorMessage}>
            {row.errorMessage}
          </span>
        ) : null}
      </div>
      <IconButton
        type="button"
        aria-label={`Удалить из списка: ${row.fileName}`}
        size="s"
        variant="danger"
        onClick={() => onRemove(row.id)}
        disabled={busy}
      >
        <DeleteIcon />
      </IconButton>
    </li>
  );
}
